import { supabase } from '../../../shared/services/supabase/client';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('sessionService');

// Singleton state for auth listener
let authSubscription = null;
let isListenerInitialized = false;
let isSessionCheckInitialized = false;

/**
 * Initialize the initial session check
 * This should be called once when the app starts
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} checkSessionAction - Action creator for checking session
 * @returns {boolean} True if session check was initialized, false if already initialized
 */
export const initializeSessionCheck = (dispatch, checkSessionAction) => {
    if (isSessionCheckInitialized) {
        logger.info('Session check already initialized');
        return false;
    }

    logger.info('Performing initial session check');
    isSessionCheckInitialized = true;
    dispatch(checkSessionAction());
    return true;
};

/**
 * Initialize the auth state change listener
 * This sets up a global listener for Supabase auth state changes
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} checkSessionAction - Action creator for checking session
 * @returns {Function|null} Cleanup function, or null if already initialized
 */
export const initializeAuthListener = (dispatch, checkSessionAction) => {
    if (isListenerInitialized) {
        logger.info('Auth listener already initialized');
        return null;
    }

    logger.info('Setting up global auth state listener');
    const { subscription } = supabase.auth.onAuthStateChange(async (event, session) => {
        logger.info('Auth state change event', { event, hasSession: !!session });

        // Check if we're on the email confirmation page
        const isOnConfirmPage = window.location.pathname === '/confirm' || 
                                 window.location.pathname.includes('/confirm');

        // Handle specific auth events
        if (event === 'PASSWORD_RECOVERY') {
            logger.info('Password recovery initiated', {
                email: session?.user?.email,
            });
            // For password recovery, we want to keep the session and not interfere
            if (session) {
                // Update the Redux state with the recovery session
                dispatch(checkSessionAction());
            }
            return;
        }

        const hashParams = new URLSearchParams(window.location.hash?.slice(1) || '');
        const isEmailConfirmation =
            hashParams.get('type') === 'signup' ||
            hashParams.get('type') === 'email' ||
            hashParams.get('type') === 'email_change';

        // Email confirmation should land on /confirm; if it hit another route, redirect first.
        if (session && isEmailConfirmation && !isOnConfirmPage) {
            logger.info('Email confirmation on non-confirm route — redirecting to /confirm', {
                path: window.location.pathname,
            });
            window.location.replace(`/confirm${window.location.search}${window.location.hash}`);
            return;
        }

        // If we're on the confirm page and a session was created (email confirmation),
        // sign out immediately to prevent auto-login
        if (isOnConfirmPage && session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
            logger.info('Email confirmation detected - signing out to prevent auto-login', {
                event,
                email: session?.user?.email,
            });
            try {
                await supabase.auth.signOut();
                logger.info('User signed out after email confirmation');
                // Don't update Redux state - let the confirm page handle it
                return;
            } catch (signOutError) {
                logger.warn('Error signing out after email confirmation', signOutError);
                // Continue with normal flow if sign out fails
            }
        }

        if (session) {
            // Update the Redux state with session details
            logger.info('Authentication session updated', {
                accessToken: session.access_token,
                sessionId: session.user?.id,
            });

            // Dispatch the checkSession action to synchronize the session details
            dispatch(checkSessionAction());
        } else {
            // If no session exists, just clear the Redux state without calling logout
            // This prevents the infinite loop since we're not triggering signOut again
            logger.info('No active session found. Clearing auth state...');
            // We'll let the Redux state be cleared by the checkSession action
            // which will be called when the component mounts
        }
    });

    authSubscription = subscription;
    isListenerInitialized = true;

    // Return cleanup function
    return () => {
        if (authSubscription) {
            logger.info('Cleaning up auth listener subscription');
            authSubscription.unsubscribe();
            authSubscription = null;
            isListenerInitialized = false;
        }
    };
};

/**
 * Cleanup the auth listener
 * This should be called when the app shuts down
 */
export const cleanupAuthListener = () => {
    if (authSubscription && typeof authSubscription.unsubscribe === 'function') {
        logger.info('Cleaning up global Supabase auth subscription');
        authSubscription.unsubscribe();
        authSubscription = null;
        isListenerInitialized = false;
        isSessionCheckInitialized = false;
    }
};

/**
 * Reset the session service state (useful for testing)
 */
export const resetSessionService = () => {
    authSubscription = null;
    isListenerInitialized = false;
    isSessionCheckInitialized = false;
};

